/**
 * Mapowanie jednej starej bazy — panel ITE (ADR-0111, faza 27, pakiet 3).
 *
 * Zakładki idą w kolejności roboty: podmioty (bez nich rekordy nie mają dokąd trafić),
 * potem pola i wartości każdej kartoteki.
 *
 * POLA I WARTOŚCI TO DWA RÓŻNE MOMENTY. Pola da się zmapować zaraz po rozpoznaniu schematu.
 * Wartości dopiero po pobraniu danych — bo ich lista pochodzi z tego, co RZECZYWIŚCIE przyszło
 * z bazy klienta, a nie ze słownika. Słownik u klienta ma kilkadziesiąt pozycji, z których
 * w kartotece występuje kilka; pokazanie całego kazałoby mapować w ciemno.
 *
 * EKRAN NIE WYMAGA KOMPLETNOŚCI. Pole bez wskazanego celu po prostu nie jedzie — to stan
 * normalny, nie brak do uzupełnienia. Dlatego zamiast walidacji „zmapuj wszystko" jest
 * licznik i filtr, bo u jednego klienta pól bywa trzydzieści, u innego ponad sto, a sensownych
 * do przeniesienia kilkanaście.
 *
 * Propozycje dopasowań są na żądanie, nigdy automatycznie: propozycja wygląda jak decyzja,
 * a wpisana samoczynnie zostałaby zatwierdzona bez przeczytania.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Wand2 } from 'lucide-react';

import { Alert } from '@/components/Alert';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { SearchInput } from '@/components/SearchInput';
import { Select } from '@/components/Select';
import { Skeleton } from '@/components/Skeleton';
import { Tabs, type TabsItem } from '@/components/Tabs';
import { LegacyValueMappingSection } from '@/components/tenant-detail/LegacyValueMappingSection';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import { extractErrorCode } from '@/lib/errors';

type LegacyEntity = 'contractor' | 'employee';
type Direction = 'to_us' | 'to_them' | 'both' | 'off';

interface SourceField {
  origin: 'core' | 'ext';
  propertyName: string;
  displayName: string;
  type: string;
  dictionaryCode: string | null;
  referenceTarget: string | null;
}

interface TargetField {
  slug: string;
  label: string;
  kind: 'core' | 'ext';
  fieldType: string;
  required: boolean;
}

interface MappingRow {
  sourceField: string;
  targetKind: 'core' | 'ext';
  targetField: string;
  direction: Direction;
  isKey: boolean;
}

interface MappingWorkspace {
  discoveredAt: string | null;
  sourceFields: SourceField[];
  targetFields: TargetField[];
  mappings: MappingRow[];
  stats: { sourceTotal: number; mapped: number };
  warnings: string[];
}

interface CompanyMapWorkspace {
  sourceCompanies: Array<{ oid: number; code: string | null; name: string; taxId: string | null }>;
  ourCompanies: Array<{ id: string; name: string; taxId: string | null }>;
  mappings: Array<{ sourceCompanyOid: number; companyId: string }>;
  stats: { sourceTotal: number; mapped: number };
}

export interface LegacyMappingModalProps {
  open: boolean;
  onClose: () => void;
  tenantId: string;
  migrationId: string;
  source: { id: string; name: string } | null;
}

export function LegacyMappingModal({
  open,
  onClose,
  tenantId,
  migrationId,
  source,
}: LegacyMappingModalProps) {
  const { t } = useTranslation();

  const basePath = source
    ? `/admin/tenants/${tenantId}/legacy-migration/${migrationId}/sources/${source.id}`
    : null;

  const items: TabsItem[] = [
    {
      value: 'companies',
      label: t('admin.tenants.legacyMigration.mapping.tabs.companies'),
      content: basePath ? <CompanyMapSection basePath={basePath} /> : null,
    },
    {
      value: 'contractor',
      label: t('admin.tenants.legacyMigration.mapping.tabs.contractors'),
      content: basePath ? <FieldMapSection basePath={basePath} entity="contractor" /> : null,
    },
    {
      value: 'contractor-values',
      label: t('admin.tenants.legacyMigration.mapping.tabs.contractorValues'),
      content: basePath ? (
        <LegacyValueMappingSection basePath={basePath} entity="contractor" />
      ) : null,
    },
    {
      value: 'employee',
      label: t('admin.tenants.legacyMigration.mapping.tabs.employees'),
      content: basePath ? <FieldMapSection basePath={basePath} entity="employee" /> : null,
    },
    {
      value: 'employee-values',
      label: t('admin.tenants.legacyMigration.mapping.tabs.employeeValues'),
      content: basePath ? (
        <LegacyValueMappingSection basePath={basePath} entity="employee" />
      ) : null,
    },
  ];

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="xl"
      title={t('admin.tenants.legacyMigration.mapping.title', { name: source?.name ?? '' })}
    >
      <Tabs items={items} defaultValue="companies" idPrefix="legacy-mapping" />
    </Modal>
  );
}

/** Wspólny komunikat, gdy rozpoznanie jeszcze nie przyszło — bez niego nie ma czego mapować. */
function BrakRozpoznania() {
  const { t } = useTranslation();
  return (
    <Alert tone="info" title={t('admin.tenants.legacyMigration.mapping.noDiscovery.title')}>
      {t('admin.tenants.legacyMigration.mapping.noDiscovery.body')}
    </Alert>
  );
}

function CompanyMapSection({ basePath }: { basePath: string }) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const queryKey = [basePath, 'companies'];

  const [draft, setDraft] = useState<Record<number, string> | null>(null);

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: async () => (await api.get<CompanyMapWorkspace>(`${basePath}/companies`)).data,
  });

  const save = useMutation({
    mutationFn: async (mappings: Array<{ sourceCompanyOid: number; companyId: string }>) => {
      await api.put(`${basePath}/companies`, { mappings });
    },
    onSuccess: () => {
      setDraft(null);
      toast.show({ variant: 'success', title: t('admin.tenants.legacyMigration.mapping.saved') });
      void queryClient.invalidateQueries({ queryKey });
    },
    onError: (err) =>
      toast.show({
        variant: 'error',
        title: t(
          `admin.tenants.legacyMigration.errors.${extractErrorCode(err)}`,
          t('admin.tenants.legacyMigration.mapping.saveFailed'),
        ),
      }),
  });

  if (isLoading) return <Skeleton className="h-40 w-full" />;
  if (!data) return null;
  if (data.sourceCompanies.length === 0) return <BrakRozpoznania />;

  const biezace: Record<number, string> =
    draft ?? Object.fromEntries(data.mappings.map((m) => [m.sourceCompanyOid, m.companyId]));

  const opcje = [
    { value: '', label: t('admin.tenants.legacyMigration.mapping.companies.skip') },
    ...data.ourCompanies.map((c) => ({ value: c.id, label: c.name })),
  ];

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-[var(--text-secondary)]">
        {t('admin.tenants.legacyMigration.mapping.companies.intro')}
      </p>

      <div className="flex flex-col gap-2">
        {data.sourceCompanies.map((firma) => (
          <div key={firma.oid} className="flex items-center gap-3">
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-sm font-medium">{firma.name}</span>
              <span className="text-xs text-[var(--text-secondary)]">
                {[firma.code, firma.taxId].filter(Boolean).join(' · ') || '—'}
              </span>
            </div>
            <Select
              className="w-72"
              options={opcje}
              value={biezace[firma.oid] ?? ''}
              onChange={(e) => setDraft({ ...biezace, [firma.oid]: e.target.value })}
              aria-label={firma.name}
            />
          </div>
        ))}
      </div>

      <div className="flex justify-end">
        <Button
          disabled={draft === null || save.isPending}
          onClick={() =>
            save.mutate(
              Object.entries(biezace)
                .filter(([, companyId]) => companyId !== '')
                .map(([oid, companyId]) => ({ sourceCompanyOid: Number(oid), companyId })),
            )
          }
        >
          {t('common.save')}
        </Button>
      </div>
    </div>
  );
}

type Filtr = 'all' | 'mapped' | 'unmapped';

function FieldMapSection({ basePath, entity }: { basePath: string; entity: LegacyEntity }) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const queryKey = [basePath, 'mappings', entity];

  const [draft, setDraft] = useState<Record<string, MappingRow> | null>(null);
  const [szukaj, setSzukaj] = useState('');
  const [filtr, setFiltr] = useState<Filtr>('all');

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: async () => (await api.get<MappingWorkspace>(`${basePath}/mappings/${entity}`)).data,
  });

  const zapisz = useMutation({
    mutationFn: async (mappings: MappingRow[]) => {
      await api.put(`${basePath}/mappings/${entity}`, { mappings });
    },
    onSuccess: () => {
      setDraft(null);
      toast.show({ variant: 'success', title: t('admin.tenants.legacyMigration.mapping.saved') });
      void queryClient.invalidateQueries({ queryKey });
    },
    onError: (err) =>
      toast.show({
        variant: 'error',
        title: t(
          `admin.tenants.legacyMigration.errors.${extractErrorCode(err)}`,
          t('admin.tenants.legacyMigration.mapping.saveFailed'),
        ),
      }),
  });

  const zaproponuj = useMutation({
    mutationFn: async () =>
      (await api.post<{ suggestions: MappingRow[] }>(`${basePath}/mappings/${entity}/suggest`))
        .data,
    onSuccess: (res) => {
      if (res.suggestions.length === 0) {
        toast.show({
          variant: 'info',
          title: t('admin.tenants.legacyMigration.mapping.noSuggestions'),
        });
        return;
      }
      setDraft((poprzedni) => {
        const baza = poprzedni ?? biezaceZBazy();
        const nowe = { ...baza };
        for (const s of res.suggestions) nowe[s.sourceField] = s;
        return nowe;
      });
      toast.show({
        variant: 'success',
        title: t('admin.tenants.legacyMigration.mapping.suggested', {
          count: res.suggestions.length,
        }),
      });
    },
  });

  function biezaceZBazy(): Record<string, MappingRow> {
    return Object.fromEntries((data?.mappings ?? []).map((m) => [m.sourceField, m]));
  }

  const biezace = draft ?? biezaceZBazy();

  const widoczne = useMemo(() => {
    const fraza = szukaj.trim().toLowerCase();
    return (data?.sourceFields ?? []).filter((pole) => {
      const zmapowane = Boolean(biezace[pole.propertyName]);
      if (filtr === 'mapped' && !zmapowane) return false;
      if (filtr === 'unmapped' && zmapowane) return false;
      if (!fraza) return true;
      return (
        pole.propertyName.toLowerCase().includes(fraza) ||
        pole.displayName.toLowerCase().includes(fraza)
      );
    });
  }, [data?.sourceFields, biezace, filtr, szukaj]);

  if (isLoading) return <Skeleton className="h-64 w-full" />;
  if (!data) return null;
  if (data.sourceFields.length === 0) return <BrakRozpoznania />;

  const zajeteCele = new Set(Object.values(biezace).map((m) => `${m.targetKind}:${m.targetField}`));

  const zmapowanych = Object.keys(biezace).length;

  return (
    <div className="flex flex-col gap-4">
      {data.warnings.length > 0 && (
        <Alert tone="warning" title={t('admin.tenants.legacyMigration.mapping.warnings')}>
          <ul className="list-disc pl-4">
            {data.warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </Alert>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="text-sm text-[var(--text-secondary)]">
          {t('admin.tenants.legacyMigration.mapping.counter', {
            mapped: zmapowanych,
            total: data.stats.sourceTotal,
          })}
        </span>
        <div className="flex items-center gap-2">
          {(['all', 'mapped', 'unmapped'] as const).map((f) => (
            <Button
              key={f}
              size="sm"
              variant={filtr === f ? 'primary' : 'secondary'}
              onClick={() => setFiltr(f)}
            >
              {t(`admin.tenants.legacyMigration.mapping.filter.${f}`)}
            </Button>
          ))}
          <Button
            size="sm"
            variant="secondary"
            leftIcon={<Wand2 className="size-4" />}
            onClick={() => zaproponuj.mutate()}
            disabled={zaproponuj.isPending}
          >
            {t('admin.tenants.legacyMigration.mapping.suggest')}
          </Button>
        </div>
      </div>

      <SearchInput
        value={szukaj}
        onChange={setSzukaj}
        placeholder={t('admin.tenants.legacyMigration.mapping.search')}
      />

      <div className="flex flex-col gap-2">
        {widoczne.length === 0 && (
          <p className="py-6 text-center text-sm text-[var(--text-secondary)]">
            {t('admin.tenants.legacyMigration.mapping.noMatches')}
          </p>
        )}

        {widoczne.map((pole) => {
          const wybrane = biezace[pole.propertyName];
          const wartosc = wybrane ? `${wybrane.targetKind}:${wybrane.targetField}` : '';

          const opcje = [
            { value: '', label: t('admin.tenants.legacyMigration.mapping.fields.skip') },
            ...data.targetFields.map((cel) => {
              const klucz = `${cel.kind}:${cel.slug}`;
              return {
                value: klucz,
                label: `${cel.label}${cel.kind === 'ext' ? ' ·' : ''}`,
                // Cel zajęty przez inne pole — blokujemy zamiast pozwolić zapisać i odbić się
                // od błędu „ten cel ma już źródło".
                disabled: klucz !== wartosc && zajeteCele.has(klucz),
              };
            }),
          ];

          return (
            <div key={pole.propertyName} className="flex flex-wrap items-center gap-3">
              <div className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-sm font-medium">{pole.displayName}</span>
                <span className="truncate text-xs text-[var(--text-secondary)]">
                  {pole.propertyName} · {pole.type}
                  {pole.referenceTarget
                    ? ` · ${t('admin.tenants.legacyMigration.mapping.fields.reference')}`
                    : ''}
                </span>
              </div>

              {pole.origin === 'core' && (
                <Badge color="gray">{t('admin.tenants.legacyMigration.mapping.fields.core')}</Badge>
              )}

              <Select
                className="w-56"
                options={opcje}
                value={wartosc}
                aria-label={pole.displayName}
                onChange={(e) => {
                  const nowe = { ...biezace };
                  if (e.target.value === '') {
                    delete nowe[pole.propertyName];
                  } else {
                    const [kind, ...reszta] = e.target.value.split(':');
                    nowe[pole.propertyName] = {
                      sourceField: pole.propertyName,
                      targetKind: kind as 'core' | 'ext',
                      targetField: reszta.join(':'),
                      direction: wybrane?.direction ?? 'to_us',
                      isKey: wybrane?.isKey ?? false,
                    };
                  }
                  setDraft(nowe);
                }}
              />

              {/*
                Kierunek ma sens dopiero, gdy pole ma dokąd jechać — przy „nie przenosimy"
                nie ma czym sterować, więc nie pokazujemy martwej kontrolki.

                Pole dodatkowe nie jedzie dziś w stronę starej bazy (silnik wypycha wyłącznie
                rdzeniowe), więc wariantów z zapisem do nich przy nim nie oferujemy — lepiej
                nie dać wyboru niż dać taki, który po cichu nic nie zrobi.
              */}
              {wartosc !== '' && (
                <Select
                  className="w-48"
                  aria-label={t('admin.tenants.legacyMigration.mapping.fields.directionLabel', {
                    field: pole.displayName,
                  })}
                  value={wybrane?.direction ?? 'to_us'}
                  options={[
                    {
                      value: 'to_us',
                      label: t('admin.tenants.legacyMigration.mapping.fields.direction.toUs'),
                    },
                    {
                      value: 'both',
                      label: t('admin.tenants.legacyMigration.mapping.fields.direction.both'),
                      disabled: wartosc.startsWith('ext:'),
                    },
                    {
                      value: 'to_them',
                      label: t('admin.tenants.legacyMigration.mapping.fields.direction.toThem'),
                      disabled: wartosc.startsWith('ext:'),
                    },
                    {
                      value: 'off',
                      label: t('admin.tenants.legacyMigration.mapping.fields.direction.off'),
                    },
                  ]}
                  onChange={(e) => {
                    if (!wybrane) return;
                    setDraft({
                      ...biezace,
                      [pole.propertyName]: {
                        ...wybrane,
                        direction: e.target.value as Direction,
                      },
                    });
                  }}
                />
              )}
            </div>
          );
        })}
      </div>

      <div className="flex justify-end">
        <Button
          disabled={draft === null || zapisz.isPending}
          onClick={() => zapisz.mutate(Object.values(biezace))}
        >
          {t('common.save')}
        </Button>
      </div>
    </div>
  );
}
